'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { useState } from 'react';

const reveal = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.6, ease: 'easeOut' },
};

const Heading = ({ eyebrow, heading, description, center }) => (
    <div
        className={`mb-8 md:mb-10 max-w-2xl ${
            center ? 'text-center mx-auto' : ''
        }`}
    >
        {eyebrow && (
            <p className="text-blue-500 font-bold text-sm mb-3">{eyebrow}</p>
        )}
        {heading && (
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 leading-tight">
                {heading}
            </h2>
        )}
        {description && (
            <p className="mt-4 text-gray-600 text-base md:text-lg">
                {description}
            </p>
        )}
    </div>
);

/* ---------------- PLAN CARD ---------------- */

const PlanCard = ({ plan, currency, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, delay: index * 0.08 }}
        className={`relative flex flex-col bg-white rounded-2xl p-6 md:p-7 shadow-sm ${
            plan.highlighted
                ? 'border-2 border-blue-500 mt-4 md:mt-0'
                : 'border border-gray-100'
        }`}
    >
        {plan.badge && (
            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-pink-500 to-indigo-400 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md">
                {plan.badge}
            </span>
        )}

        <div className="text-center">
            <h3 className="font-bold text-gray-800 text-xl">{plan.name}</h3>
            <p className="text-gray-600 text-sm mt-1.5 mb-4">
                {plan.description}
            </p>

            {plan.providers && (
                <p className="flex items-center justify-center gap-1.5 text-blue-500 text-xs font-semibold mb-4">
                    <i className="ri-cloud-line text-base"></i>
                    {plan.providers}
                </p>
            )}

            <p className="text-gray-500 text-sm mb-1">Starting from</p>
            <p className="text-gray-800 leading-none">
                <span className="text-4xl font-bold">
                    {plan.price[currency]}
                </span>{' '}
                <span className="text-sm text-gray-500 font-medium">
                    / month
                </span>
            </p>
        </div>

        <ul className="grid gap-2.5 my-6">
            {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2.5">
                    <i className="ri-checkbox-circle-fill text-blue-500 text-lg mt-0.5 shrink-0 leading-none"></i>
                    <span className="text-gray-700 text-sm font-medium">
                        {feature}
                    </span>
                </li>
            ))}
        </ul>

        <div className="mt-auto">
            {plan.learnMore && (
                <a
                    href={plan.learnMore.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-blue-500 text-sm font-semibold underline mb-4"
                >
                    {plan.learnMore.text} →
                </a>
            )}
            <Link href="/contact-sales" className="block">
                {plan.highlighted ? (
                    <button className="hello_button w-full py-3 px-6 bg-blue-500 rounded-md shadow-xl hover:shadow-none transition duration-300">
                        <span className="font-bold text-white text-sm md:text-base">
                            {plan.buttonText ?? 'Talk to an Expert'}
                        </span>
                    </button>
                ) : (
                    <button className="w-full py-3 px-6 border border-gray-200 rounded-md hover:border-blue-500 transition duration-300">
                        <span className="font-bold text-gray-800 text-sm md:text-base">
                            {plan.buttonText ?? 'Talk to an Expert'}
                        </span>
                    </button>
                )}
            </Link>
        </div>
    </motion.div>
);

/* ---------------- FAQ ---------------- */

const PricingFaq = ({ faq }) => {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <div className="space-y-4">
            {faq.items.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                    <div
                        key={item.question}
                        className={`rounded-xl border transition-colors ${
                            isOpen
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-100 bg-gray-50'
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => setOpenIndex(isOpen ? -1 : index)}
                            aria-expanded={isOpen}
                            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                        >
                            <span className="font-bold text-gray-800 text-base md:text-lg">
                                {item.question}
                            </span>
                            <span className="shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center">
                                <i
                                    className={
                                        isOpen
                                            ? 'ri-subtract-line'
                                            : 'ri-add-line'
                                    }
                                ></i>
                            </span>
                        </button>
                        {isOpen && (
                            <p className="px-6 pb-5 text-gray-600 text-sm md:text-base">
                                {item.answer}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/* ---------------- MAIN ---------------- */

export default function PricingSection({ pricing }) {
    const { currencies = [], plans = [], glance, comparison, faq } = pricing;
    const [currency, setCurrency] = useState(currencies[0]?.key ?? 'inr');

    return (
        <>
            <section
                id="pricing"
                className="w-full py-10 md:py-14 px-4 bg-white"
            >
                <motion.div className="max-w-6xl mx-auto" {...reveal}>
                    <Heading
                        eyebrow={pricing.eyebrow}
                        heading={pricing.heading}
                        description={pricing.description}
                        center
                    />

                    {currencies.length > 1 && (
                        <div
                            role="group"
                            aria-label="Currency"
                            className="flex w-fit mx-auto border border-gray-200 rounded-full p-1 bg-white mb-8"
                        >
                            {currencies.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    aria-pressed={currency === c.key}
                                    onClick={() => setCurrency(c.key)}
                                    className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
                                        currency === c.key
                                            ? 'bg-blue-500 text-white'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {plans.map((plan, i) => (
                            <PlanCard
                                key={plan.name}
                                plan={plan}
                                currency={currency}
                                index={i}
                            />
                        ))}
                    </div>

                    {glance && (
                        <div className="max-w-4xl mx-auto mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-r-2xl p-5 md:p-6">
                            <p className="text-blue-600 font-bold text-xs uppercase tracking-wide mb-1.5">
                                {glance.label}
                            </p>
                            <p className="text-gray-700 font-medium">
                                {glance.text}
                            </p>
                        </div>
                    )}
                </motion.div>
            </section>

            {comparison && (
                <section className="w-full py-10 md:py-14 px-4 bg-blue-50/40">
                    <motion.div className="max-w-6xl mx-auto" {...reveal}>
                        <Heading
                            eyebrow={comparison.eyebrow}
                            heading={comparison.heading}
                            description={comparison.description}
                        />
                        <div className="overflow-x-auto bg-white border border-gray-100 rounded-2xl shadow-sm">
                            <table className="w-full min-w-[560px] text-sm border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="w-1/4"></th>
                                        {comparison.columns.map((col, i) => (
                                            <th
                                                key={col}
                                                className={`text-left font-bold text-gray-800 px-5 py-4 ${
                                                    i ===
                                                    comparison.columns.length -
                                                        1
                                                        ? 'bg-blue-50'
                                                        : ''
                                                }`}
                                            >
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {comparison.rows.map((row) => (
                                        <tr
                                            key={row.label}
                                            className="border-b border-gray-100 last:border-b-0"
                                        >
                                            <td className="px-5 py-4 font-semibold text-gray-800 align-top">
                                                {row.label}
                                            </td>
                                            {row.values.map((value, i) => (
                                                <td
                                                    key={i}
                                                    className={`px-5 py-4 text-gray-600 align-top ${
                                                        i ===
                                                        row.values.length - 1
                                                            ? 'bg-blue-50'
                                                            : ''
                                                    }`}
                                                >
                                                    {value}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                </section>
            )}

            {faq && (
                <section className="w-full py-10 md:py-14 px-4 bg-white">
                    <motion.div className="max-w-3xl mx-auto" {...reveal}>
                        <Heading eyebrow={faq.eyebrow} heading={faq.heading} />
                        <PricingFaq faq={faq} />
                    </motion.div>
                </section>
            )}
        </>
    );
}
